"use client";
import { useState, useRef } from "react";
import axios from "axios";
import { ModalCustom } from "../../../components/custom/modal-custom";
import { showToast } from "../../../components/custom/custom-toast";
import { ApiConfig, API_USER_ROUTER } from "../../../services/api-config";
import CookieUtils from "../../../utils/cookie-utils";

interface ImportResult {
  success: number;
  failed: number;
  errors: Array<{ row: number; message: string }>;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const UserImportModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [skipErrors, setSkipErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const token = CookieUtils.getCurrentUser().access_token ?? "";
  const headers = {
    Authorization: `Bearer ${token}`,
    "x-svc-id": ApiConfig.PROJECT_ID.USER_SERVICE,
    platform: "1",
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setSkipErrors(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(
        `${ApiConfig.API_URL}/api/${API_USER_ROUTER.API_EXCEL_TEMPLATE}`,
        { headers, responseType: "arraybuffer" }
      );
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "mau-nhap-nhan-vien.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      showToast({ message: "Lỗi tải file mẫu", type: "error" });
    } finally {
      setDownloading(false);
    }
  };

  const handleImport = async () => {
    if (!file) {
      showToast({ message: "Vui lòng chọn file Excel", type: "error" });
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("skip_errors", skipErrors ? "true" : "false");

      const res = await axios.post(
        `${ApiConfig.API_URL}/api/${API_USER_ROUTER.API_EXCEL_IMPORT}`,
        formData,
        {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
          validateStatus: () => true, // Không throw cho bất kỳ status nào
        }
      );

      if (res.status >= 400) {
        // Lỗi từ server — show toast, không throw
        const msg = res.data?.message || res.data?.data?.message || `Lỗi ${res.status}`;
        showToast({ message: msg, type: "error" });
        setLoading(false);
        return;
      }

      const data: ImportResult = res.data?.data || res.data;
      if (data && typeof data.success === "number") {
        setResult(data);
        if (data.success > 0) {
          showToast({ message: `Import thành công ${data.success} nhân viên`, type: "success" });
          onSuccess?.();
        }
        if (data.failed > 0 && data.success === 0) {
          showToast({ message: `Import thất bại: ${data.failed} dòng lỗi`, type: "error" });
        }
      } else {
        showToast({ message: "Phản hồi không hợp lệ từ server", type: "error" });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Lỗi import";
      showToast({ message: msg, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const bodyModal = (
    <div className="space-y-5">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-800 font-medium mb-2">
          Bước 1: Tải file mẫu Excel, điền thông tin nhân viên
        </p>
        <button
          type="button"
          onClick={handleDownloadTemplate}
          disabled={downloading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
          {downloading ? "Đang tải..." : "Tải file mẫu (.xlsx)"}
        </button>
        <p className="text-xs text-blue-600 mt-2">
          Mật khẩu mặc định cho nhân viên mới: <strong>000000</strong>
        </p>
      </div>

      <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-4">
        <p className="text-sm text-gray-700 font-medium mb-2">
          Bước 2: Chọn file Excel đã điền thông tin
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
        />
        {file && (
          <p className="mt-2 text-sm text-green-600 font-medium">
            Đã chọn: {file.name} ({(file.size / 1024).toFixed(1)} KB)
          </p>
        )}
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={skipErrors}
          onChange={(e) => setSkipErrors(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
        />
        <span className="text-sm text-gray-700">Bỏ qua dòng lỗi (import các dòng hợp lệ)</span>
      </label>

      {result && (
        <div className="border rounded-xl overflow-hidden">
          <div className="flex gap-4 p-4 bg-gray-50">
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full bg-green-500" />
              <span className="text-sm font-bold text-green-700">Thành công: {result.success}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full bg-red-500" />
              <span className="text-sm font-bold text-red-700">Lỗi: {result.failed}</span>
            </div>
          </div>
          {result.errors.length > 0 && (
            <div className="max-h-48 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-red-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-red-800 w-16">Dòng</th>
                    <th className="px-3 py-2 text-left font-medium text-red-800">Lỗi</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.map((err, idx) => (
                    <tr key={idx} className="border-t border-red-100">
                      <td className="px-3 py-1.5 text-red-600 font-mono">{err.row}</td>
                      <td className="px-3 py-1.5 text-red-700">{err.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const footerModal = (
    <div className="flex gap-2">
      {result && result.success > 0 ? (
        <button type="button" onClick={handleClose}
          className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">
          Hoàn tất
        </button>
      ) : (
        <button type="button" onClick={handleImport} disabled={!file || loading}
          className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50">
          {loading ? "Đang import..." : "Import"}
        </button>
      )}
      <button type="button" onClick={handleClose}
        className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">
        Đóng
      </button>
    </div>
  );

  return (
    <ModalCustom
      titleModal="Nhập nhân viên từ Excel"
      isOpen={isOpen}
      onClose={() => handleClose()}
      bodyModal={bodyModal}
      footerModal={footerModal}
      modalSize="lg"
    />
  );
};

export default UserImportModal;
